import { Button, cn } from '@spiaggeit/spit-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BackButton } from '@/components/BackButton/index.tsx'
import { ButtonLink } from '@/components/ButtonLink'
import { useAppSelector } from '@/hooks/store.ts'
import { usePolicyAcceptance } from '@/hooks/usePolicyAcceptance.ts'
import {
  AB_PRIVACY_DISCLAIMER_CONTROL_CLASS,
  usePrivacyAbVariant,
} from '@/hooks/usePrivacyAbVariant.ts'
import { useRefundPolicyDialogProps } from '@/hooks/useRefundPolicyDialogProps.ts'
import { isKioskSelector } from '@/store/appSlice.ts'
import { cartSlice } from '@/store/cartSlice/index.ts'
import { licenseSlice } from '@/store/licenseSlice'

import { PaymentMethod } from '@/store/selectors/selectPaymentMethods.ts'
import { formatPrice } from '@/utils/price.ts'
import { BookingPolicyDialog } from '../../BookingPolicyDialog'
import { PolicyAcceptanceCheckbox } from '../../PolicyAcceptanceCheckbox'
import { RefundPolicyDialog } from '../../RefundPolicyDialog'
import { RefundPolicy } from '../../SelectedElementDrawer/RefundPolicy.tsx'
import { TranslatedPolicyText } from '../../TranslatedPolicyText'
export interface ReservationFooterProps {
  nextPath?: string
  isFinalPayment?: boolean
  isDisabled?: boolean
  requirePolicyAcceptance?: boolean
  total: number
  paymentMethod?: PaymentMethod
  className?: string
}

export const ReservationFooter = (props: ReservationFooterProps) => {
  const license = useAppSelector(licenseSlice.selectors.license)
  const beachPrivacyPolicy = license?.bookingPolicyUrl
  const regulation = license?.bookingPolicy
  const isKiosk = useAppSelector(isKioskSelector)
  const isPayingWithCard = useAppSelector(cartSlice.selectors.isPayingWithCard)
  const isCreatingCart = useAppSelector(cartSlice.selectors.isCreateCartPending)
  const isPayingWithStripe = useAppSelector(
    cartSlice.selectors.isPayingWithStripe
  )

  const isLoading = isPayingWithCard || isCreatingCart || isPayingWithStripe

  const { t } = useTranslation()

  const [showDialogStatus, setShowDialogStatus] = useState<
    'closed' | 'booking-policy' | 'refund-policy'
  >('closed')
  const [showPolicyAcceptanceError, setShowPolicyAcceptanceError] =
    useState(false)
  const { isPolicyAccepted, setIsPolicyAccepted } = usePolicyAcceptance()
  const privacyAbVariant = usePrivacyAbVariant()
  const isPrivacyCheckboxVariant = privacyAbVariant === 'checkbox'

  const footerRef = useRef<HTMLElement>(null)
  const isPaymentCta = !props.nextPath
  const showPrivacyAbBlock = props.requirePolicyAcceptance && isPaymentCta
  const shouldRequirePolicyAcceptance =
    showPrivacyAbBlock && isPrivacyCheckboxVariant
  const isSubmitDisabled = props.isDisabled

  const handlePolicyCheckedChange = (accepted: boolean) => {
    setIsPolicyAccepted(accepted)
    if (accepted) {
      setShowPolicyAcceptanceError(false)
    }
  }

  useEffect(() => {
    if (!shouldRequirePolicyAcceptance) {
      setShowPolicyAcceptanceError(false)
      return
    }

    const form = footerRef.current?.closest('form')
    if (!form) return

    const handleSubmit = (event: SubmitEvent) => {
      if (!isPolicyAccepted) {
        event.preventDefault()
        event.stopPropagation()
        setShowPolicyAcceptanceError(true)
      }
    }

    form.addEventListener('submit', handleSubmit)
    return () => form.removeEventListener('submit', handleSubmit)
  }, [shouldRequirePolicyAcceptance, isPolicyAccepted])

  const ctaLabel = useMemo(() => {
    const formattedPrice = formatPrice(props.total)

    if (isKiosk) {
      return t('cta.goToPayment')
    }

    if (props.isFinalPayment) {
      if (props.paymentMethod === 'bank-transfer') {
        return t('cta.payWithBankTransfer')
      }

      if (props.paymentMethod === 'beach') {
        return t('cta.beachPayment')
      }

      return t('cta.confirmAndPay', { price: formattedPrice })
    }

    return t('cta.goToPaymentWithPrice', { price: formattedPrice })
  }, [props.total, props.isFinalPayment, props.paymentMethod])

  const refundPolicyDialogProps = useRefundPolicyDialogProps()

  return (
    <footer
      className={cn('flex flex-col gap-2', props.className, {
        'mt-auto py-4': isKiosk,
      })}
      ref={footerRef}
    >
      {!isKiosk && <RefundPolicy />}

      <div
        className={cn('flex flex-col gap-1', {
          'flex-col-reverse gap-4': isKiosk,
        })}
      >
        {showPrivacyAbBlock && isPrivacyCheckboxVariant && (
          <PolicyAcceptanceCheckbox
            beachPrivacyPolicy={beachPrivacyPolicy}
            checked={isPolicyAccepted}
            className={cn({ 'mb-2': !isKiosk })}
            hasError={showPolicyAcceptanceError}
            onBookingPolicyDialogOpen={() =>
              setShowDialogStatus('booking-policy')
            }
            onCheckedChange={handlePolicyCheckedChange}
            onRefundPolicyDialogOpen={() =>
              setShowDialogStatus('refund-policy')
            }
            regulation={regulation}
          />
        )}

        <div className={cn('flex', { 'gap-4': isKiosk })}>
          {isKiosk && <BackButton />}

          {props.nextPath ? (
            <ButtonLink
              className="flex-1"
              data-testid="recap-submit"
              icon="ArrowRight"
              label={t('cta.continue')}
              to={props.nextPath}
            />
          ) : (
            <Button
              className="flex-1"
              data-testid="recap-submit"
              disabled={isSubmitDisabled}
              isLoading={isLoading}
              type="submit"
            >
              {ctaLabel}
            </Button>
          )}
        </div>

        {showPrivacyAbBlock && !isPrivacyCheckboxVariant && (
          <p
            className={cn(
              'text-xs text-secondary',
              AB_PRIVACY_DISCLAIMER_CONTROL_CLASS
            )}
          >
            <TranslatedPolicyText
              beachPrivacyPolicy={beachPrivacyPolicy}
              onBookingPolicyDialogOpen={() =>
                setShowDialogStatus('booking-policy')
              }
              onRefundPolicyDialogOpen={() =>
                setShowDialogStatus('refund-policy')
              }
              regulation={regulation}
            />
          </p>
        )}
      </div>

      <BookingPolicyDialog
        bookingPolicy={regulation}
        isOpen={showDialogStatus === 'booking-policy'}
        setIsOpen={(isOpen) =>
          setShowDialogStatus(isOpen ? 'booking-policy' : 'closed')
        }
      />

      <RefundPolicyDialog
        {...refundPolicyDialogProps}
        isOpen={showDialogStatus === 'refund-policy'}
        setIsOpen={(isOpen) =>
          setShowDialogStatus(isOpen ? 'refund-policy' : 'closed')
        }
      />
    </footer>
  )
}
